/**
 * Pure mapper from FHIR `Appointment` → provider calendar UI event.
 *
 * Centralizes all FHIR parsing for the calendar so React components don't
 * reach into participant/status/type details directly.
 *
 * First-cut scope (see external Appointments PRD):
 * - Only clinical client↔clinician appointments.
 * - Status values supported on the read path:
 *   `proposed` | `booked` | `fulfilled` | `cancelled` | `noshow`.
 *   Other FHIR statuses (pending, arrived, checked-in, entered-in-error,
 *   waitlist) are mapped conservatively so the calendar never crashes.
 * - Recurrence, rooms/devices, provider availability, and telehealth/video
 *   behavior are intentionally out of scope.
 */

import type { Appointment, AppointmentParticipant } from '@medplum/fhirtypes';
import { providerCalendarEventSchema } from '@/schemas/calendar-event.schema';
import type {
  ProviderCalendarEvent,
  ProviderCalendarEventCategory,
  ProviderCalendarEventModality,
  ProviderCalendarEventStatus,
  ProviderCalendarEventType,
} from './types';
import {
  APPOINTMENT_SERVICE_CATEGORY_INTAKE,
  APPOINTMENT_SERVICE_CATEGORY_SYSTEM,
  APPOINTMENT_SERVICE_TYPE_SYSTEM,
  CALENDAR_EVENT_CATEGORY_SYSTEM,
  CANCELLATION_REASON_SYSTEM,
  EPISODE_OF_CARE_CONTEXT_EXT,
  SCREENING_CALL_SERVICE_TYPE_CODE,
} from '@unify/fhir-model/constants';
import { getVideoMeetingUrl } from '@unify/fhir-model/resources/appointment';
import { APPOINTMENT_CATEGORY_SYSTEM } from './saveCalendarAppointment';
import {
  isHumanParticipantDisplay,
  patientDisplayLabelFromReference,
} from '../../pages/appointments/utils/fhirHelpers';
import { getAppointmentGroupRef } from '../../lib/groupTherapy/groupSharedNote';

const PARTICIPANT_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType';
const ROOM_PARTICIPANT_TYPE_SYSTEM = 'https://unify.health/fhir/CodeSystem/participant-type';

/** Identifier for references of the shape `ResourceType/id`. */
function parseReference(
  reference: string | undefined,
  resourceType: string,
): string | undefined {
  if (!reference) return undefined;
  const prefix = `${resourceType}/`;
  if (!reference.startsWith(prefix)) return undefined;
  const id = reference.slice(prefix.length).trim();
  return id || undefined;
}

function findParticipantByActorType(
  participants: AppointmentParticipant[] | undefined,
  resourceType: string,
): AppointmentParticipant | undefined {
  return (participants ?? []).find((p) =>
    p.actor?.reference?.startsWith(`${resourceType}/`),
  );
}

function participantHasType(
  participant: AppointmentParticipant | undefined,
  system: string,
  code: string,
): boolean {
  return (
    participant?.type?.some((type) =>
      type.coding?.some((coding) => coding.system === system && coding.code === code),
    ) ?? false
  );
}

function resolveSiteParticipant(
  participants: AppointmentParticipant[] | undefined,
): AppointmentParticipant | undefined {
  return (participants ?? []).find((participant) =>
    participant.actor?.reference?.startsWith('Location/') &&
    participantHasType(participant, PARTICIPANT_TYPE_SYSTEM, 'LOC'),
  );
}

function resolveRoomParticipant(
  participants: AppointmentParticipant[] | undefined,
): AppointmentParticipant | undefined {
  return (participants ?? []).find((participant) =>
    participant.actor?.reference?.startsWith('Location/') &&
    participantHasType(participant, ROOM_PARTICIPANT_TYPE_SYSTEM, 'ROOM'),
  );
}

function resolveDeviceParticipants(
  participants: AppointmentParticipant[] | undefined,
): AppointmentParticipant[] {
  return (participants ?? []).filter((participant) =>
    participant.actor?.reference?.startsWith('Device/') &&
    participantHasType(participant, PARTICIPANT_TYPE_SYSTEM, 'DEV'),
  );
}

/**
 * Narrow-range status mapping per the external Appointments PRD. Downstream
 * rendering (colors / strikethrough) uses the returned calendar status codes;
 * the page's local type accepts more statuses (e.g. `clinician-cancelled`),
 * but without richer signal we default `cancelled` to `client-cancelled`.
 */
export function mapAppointmentStatusToCalendarStatus(
  fhirStatus: Appointment['status'] | undefined,
  cancelationReasonCode?: string,
): ProviderCalendarEventStatus | undefined {
  switch (fhirStatus) {
    case 'proposed':
      return 'proposed';
    case 'booked':
    case 'pending':
    case 'arrived':
    case 'checked-in':
      return 'scheduled';
    case 'fulfilled':
      return 'attended';
    case 'cancelled':
      if (cancelationReasonCode === 'clinician-cancelled') return 'clinician-cancelled';
      if (cancelationReasonCode === 'client-late-cancelled') return 'client-late-cancelled';
      return 'client-cancelled';
    case 'noshow':
      return 'client-no-show';
    case 'entered-in-error':
      return 'entered-in-error';
    default:
      return undefined;
  }
}

const APPOINTMENT_STATUS_COLOR_MAP: Record<ProviderCalendarEventStatus, string> = {
  proposed: 'violet',
  scheduled: 'blue',
  attended: 'green',
  'client-cancelled': 'red',
  'client-late-cancelled': 'red',
  'clinician-cancelled': 'yellow',
  'client-no-show': 'red',
  'entered-in-error': 'red',
};

/**
 * Resolve modality from `Appointment.appointmentType` or service-type codings.
 * Purely cosmetic for rendering; live telehealth/video join behavior is out of
 * scope for this first cut.
 */
export function resolveModality(appointment: Appointment): ProviderCalendarEventModality {
  const apptTypeCodings = appointment.appointmentType?.coding ?? [];
  for (const coding of apptTypeCodings) {
    const display = coding.display?.toLowerCase() ?? '';
    if (display.includes('hybrid')) return 'hybrid';
    if (coding.code === 'VR' && display.includes('hybrid')) return 'hybrid';
    if (display.includes('telehealth') || display.includes('virtual')) {
      return 'telehealth';
    }
    if (coding.code === 'VR') return 'telehealth';
  }

  const apptTypeText = appointment.appointmentType?.text?.toLowerCase() ?? '';
  if (apptTypeText.includes('hybrid')) {
    return 'hybrid';
  }
  if (apptTypeText.includes('telehealth') || apptTypeText.includes('virtual')) {
    return 'telehealth';
  }

  return 'in-person';
}

function resolveServiceTypeDisplay(appointment: Appointment): string | undefined {
  const serviceType = appointment.serviceType?.[0];
  const coding = serviceType?.coding?.[0];
  return (
    coding?.display?.trim() ||
    serviceType?.text?.trim() ||
    appointment.appointmentType?.text?.trim() ||
    appointment.appointmentType?.coding?.[0]?.display?.trim() ||
    undefined
  );
}

function resolveSessionHealthcareServiceId(appointment: Appointment): string | undefined {
  for (const serviceType of appointment.serviceType ?? []) {
    const coding = serviceType.coding?.find((c) => c.system === CALENDAR_EVENT_CATEGORY_SYSTEM);
    if (coding?.code?.trim()) return coding.code.trim();
  }
  return undefined;
}

function resolveServiceTypeCode(appointment: Appointment): string | undefined {
  for (const serviceType of appointment.serviceType ?? []) {
    for (const coding of serviceType.coding ?? []) {
      if (coding.system === CALENDAR_EVENT_CATEGORY_SYSTEM) continue;
      if (coding.code?.trim()) return coding.code.trim();
    }
  }
  return undefined;
}

const VALID_CATEGORIES = new Set<string>(['session', 'group', 'meeting', 'time-entry']);

function resolveAppointmentCategory(appointment: Appointment): ProviderCalendarEventCategory | undefined {
  const codings = appointment.serviceCategory?.flatMap((sc) => sc.coding ?? []) ?? [];
  const match = codings.find(
    (c) => c.system === APPOINTMENT_CATEGORY_SYSTEM && c.code && VALID_CATEGORIES.has(c.code),
  );
  return match?.code as ProviderCalendarEventCategory | undefined;
}

function categoryToEventType(category: ProviderCalendarEventCategory | undefined): ProviderCalendarEventType {
  switch (category) {
    case 'meeting':
      return 'meeting';
    case 'time-entry':
      return 'blocked';
    default:
      return 'appointment';
  }
}

/**
 * Intake appointments (e.g. the high-risk screening call written by the
 * `high-risk-intake-scheduling` bot) link to the screening HealthcareService via
 * a `serviceType` HS-id coding under {@link CALENDAR_EVENT_CATEGORY_SYSTEM} —
 * exactly like a normal clinical session — but they carry `serviceCategory=intake`
 * (a *different* CodeSystem than the calendar's own category system, so
 * {@link resolveAppointmentCategory} returns `undefined` and the page would
 * otherwise default them to an editable `session`).
 *
 * Intake semantics — notably the recurring-assessment skip guard — depend on
 * `serviceCategory=intake`. We detect intake here and surface these as read-only
 * (`isInvitedEvent`) so a manual edit/save on the provider calendar can never
 * silently reclassify them into a `session` (which would drop both
 * `serviceCategory=intake` and the `screening-call` coding). Normal
 * session/group/meeting/time-entry appointments are unaffected.
 */
function isIntakeAppointment(appointment: Appointment): boolean {
  const isIntakeCategory = appointment.serviceCategory?.some((cc) =>
    cc.coding?.some(
      (c) =>
        c.system === APPOINTMENT_SERVICE_CATEGORY_SYSTEM &&
        c.code === APPOINTMENT_SERVICE_CATEGORY_INTAKE,
    ),
  );
  const isScreeningCall = appointment.serviceType?.some((st) =>
    st.coding?.some(
      (c) =>
        c.system === APPOINTMENT_SERVICE_TYPE_SYSTEM &&
        c.code === SCREENING_CALL_SERVICE_TYPE_CODE,
    ),
  );
  return Boolean(isIntakeCategory || isScreeningCall);
}

/**
 * Extract additional participants (ATND / SPRF) beyond the primary practitioner
 * and primary patient. Used for meeting participants and group attendees.
 */
function resolveAdditionalParticipants(
  participants: AppointmentParticipant[] | undefined,
  primaryPractitionerRef: string | undefined,
  primaryPatientRef: string | undefined,
): ProviderCalendarEvent['additionalParticipants'] {
  if (!participants) return undefined;
  const extras: NonNullable<ProviderCalendarEvent['additionalParticipants']> = [];
  for (const p of participants) {
    const ref = p.actor?.reference;
    if (!ref) continue;
    if (ref === primaryPractitionerRef || ref === primaryPatientRef) continue;
    const [resourceType, id] = ref.split('/');
    if (!resourceType || !id) continue;
    extras.push({ resourceType, id, display: p.actor?.display?.trim() });
  }
  return extras.length > 0 ? extras : undefined;
}

function resolveEpisodeOfCareId(appointment: Appointment): string | undefined {
  const ref = appointment.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXT)
    ?.valueReference?.reference;
  return parseReference(ref, 'EpisodeOfCare');
}

function resolveLocationLabel(participants: AppointmentParticipant[] | undefined): string | undefined {
  const locationParticipant = resolveSiteParticipant(participants)
    ?? resolveRoomParticipant(participants)
    ?? findParticipantByActorType(participants, 'Location');
  const display = locationParticipant?.actor?.display?.trim();
  return display || undefined;
}

function resolveSiteId(participants: AppointmentParticipant[] | undefined): string | undefined {
  return parseReference(resolveSiteParticipant(participants)?.actor?.reference, 'Location');
}

function resolveRoomId(participants: AppointmentParticipant[] | undefined): string | undefined {
  return parseReference(resolveRoomParticipant(participants)?.actor?.reference, 'Location');
}

function resolveDeviceIds(participants: AppointmentParticipant[] | undefined): string[] | undefined {
  const deviceIds = resolveDeviceParticipants(participants)
    .map((participant) => parseReference(participant.actor?.reference, 'Device'))
    .filter(Boolean) as string[];
  return deviceIds.length > 0 ? deviceIds : undefined;
}

/** Display names of every Patient participant, in `Appointment.participant` order. */
function resolvePatientDisplays(
  participants: AppointmentParticipant[] | undefined,
  patientDisplayById?: ReadonlyMap<string, string>,
): string[] {
  const displays: string[] = [];
  for (const p of participants ?? []) {
    const id = parseReference(p.actor?.reference, 'Patient');
    if (!id) continue;
    displays.push(
      patientDisplayLabelFromReference(
        p.actor?.reference,
        p.actor?.display?.trim() || patientDisplayById?.get(id)?.trim(),
      ),
    );
  }
  return displays;
}

function buildTitle(params: {
  serviceTypeDisplay: string | undefined;
  clientDisplay: string | undefined;
  description: string | undefined;
  therapyGroupDisplay: string | undefined;
}): string {
  const { serviceTypeDisplay, clientDisplay, description, therapyGroupDisplay } = params;
  if (therapyGroupDisplay) {
    return therapyGroupDisplay;
  }
  if (clientDisplay && serviceTypeDisplay) {
    return `${clientDisplay} — ${serviceTypeDisplay}`;
  }
  if (clientDisplay) {
    return clientDisplay;
  }
  if (serviceTypeDisplay) return serviceTypeDisplay;
  if (description?.trim()) return description.trim();
  return 'Appointment';
}

function resolveOwnerPractitionerParticipant(
  participants: AppointmentParticipant[] | undefined,
): AppointmentParticipant | undefined {
  const withPprf = (participants ?? []).find(
    (participant) =>
      participant.actor?.reference?.startsWith('Practitioner/') &&
      participantHasType(participant, PARTICIPANT_TYPE_SYSTEM, 'PPRF'),
  );
  if (withPprf) return withPprf;
  return findParticipantByActorType(participants, 'Practitioner');
}

export interface AppointmentMapperOptions {
  /**
   * Display label to use for `event.provider` when the appointment's
   * practitioner matches the current logged-in clinician.
   */
  currentProviderDisplayLabel?: string;
  /** Logged-in practitioner's FHIR id; used to detect "my" appointments. */
  currentPractitionerId?: string;
  /**
   * Per-owner manage permission. When omitted or still empty (access not yet
   * resolved), `canManage` defaults to `!isInvitedEvent` (own calendar /
   * invited semantics).
   */
  canManageByOwnerId?: ReadonlyMap<string, boolean>;
  /**
   * Patient ids the viewer may see. When set and a patient is present but not
   * included, titles/clients are redacted to `Busy`.
   */
  readablePatientIds?: ReadonlySet<string>;
  /**
   * Patient id → display name. Used when `Appointment.participant.actor.display`
   * is missing (common for bot-generated group sessions).
   */
  patientDisplayById?: ReadonlyMap<string, string>;
  /** Practitioner id → live staff display label. */
  practitionerDisplayById?: ReadonlyMap<string, string>;
}

/**
 * Map a FHIR `Appointment` to a calendar UI event.
 *
 * Returns `null` if the appointment is missing the fundamental fields required
 * for rendering (id / start / end), rather than producing an invalid event.
 */
export function appointmentToCalendarEvent(
  appointment: Appointment,
  options: AppointmentMapperOptions = {},
): ProviderCalendarEvent | null {
  if (!appointment.id || !appointment.start || !appointment.end) return null;

  const start = new Date(appointment.start);
  const end = new Date(appointment.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

  const participants = appointment.participant;
  const patientParticipant = findParticipantByActorType(participants, 'Patient');
  const practitionerParticipant = resolveOwnerPractitionerParticipant(participants);

  const patientId = parseReference(patientParticipant?.actor?.reference, 'Patient');
  const practitionerId = parseReference(
    practitionerParticipant?.actor?.reference,
    'Practitioner',
  );
  const ownerPractitionerId = practitionerId;

  let clientDisplay =
    patientParticipant?.actor?.display?.trim() ||
    (patientId ? options.patientDisplayById?.get(patientId)?.trim() : undefined);
  const rawProviderDisplay = practitionerParticipant?.actor?.display?.trim();
  const providerLabelFromParticipant = isHumanParticipantDisplay(rawProviderDisplay, practitionerId)
    ? rawProviderDisplay
    : undefined;
  const providerLabelFromResource = practitionerId
    ? options.practitionerDisplayById?.get(practitionerId)
    : undefined;

  /** Keep filter semantics simple: when this appointment belongs to the current
   * user's Practitioner, surface the caller-supplied label so the page's
   * "my-calendar" filter (which compares to a constant) continues to work. */
  const providerLabel =
    providerLabelFromResource ??
    (options.currentPractitionerId && practitionerId === options.currentPractitionerId
      ? options.currentProviderDisplayLabel ?? providerLabelFromParticipant
      : providerLabelFromParticipant);

  const status = mapAppointmentStatusToCalendarStatus(
    appointment.status,
    appointment.cancelationReason?.coding?.find((c) => c.system === CANCELLATION_REASON_SYSTEM)?.code,
  );
  const serviceTypeDisplay = resolveServiceTypeDisplay(appointment);
  const serviceTypeCode = resolveServiceTypeCode(appointment);
  const sessionHealthcareServiceId = resolveSessionHealthcareServiceId(appointment);
  const modality = resolveModality(appointment);
  const locationLabel = resolveLocationLabel(participants);
  const siteId = resolveSiteId(participants);
  const roomId = resolveRoomId(participants);
  const deviceIds = resolveDeviceIds(participants);
  const appointmentCategory = resolveAppointmentCategory(appointment);
  const eventType = categoryToEventType(appointmentCategory);
  const additionalParticipants = resolveAdditionalParticipants(
    participants,
    practitionerParticipant?.actor?.reference,
    patientParticipant?.actor?.reference,
  );

  const groupRef = getAppointmentGroupRef(appointment);
  const therapyGroupId = parseReference(groupRef?.reference, 'Group');
  const therapyGroupDisplay = therapyGroupId ? groupRef?.display?.trim() || undefined : undefined;
  let clients =
    appointmentCategory === 'group'
      ? resolvePatientDisplays(participants, options.patientDisplayById)
      : undefined;

  let title = buildTitle({
    serviceTypeDisplay,
    clientDisplay,
    description: appointment.description,
    therapyGroupDisplay,
  });

  const color = status ? APPOINTMENT_STATUS_COLOR_MAP[status] : 'blue';

  /** Intake/screening calls are surfaced read-only (see {@link isIntakeAppointment})
   * so an edit/save can't reclassify them; otherwise an event is read-only only
   * when the current user is a non-owner participant ("invited"). */
  const isInvitedEvent =
    isIntakeAppointment(appointment) ||
    (options.currentPractitionerId != null &&
      practitionerId !== options.currentPractitionerId);

  /** Resolved calendar access always contains the viewer, so an empty map means
   * access is still loading — fall back to own-calendar semantics instead of
   * marking every event read-only. */
  const resolvedAccess = options.canManageByOwnerId?.size ? options.canManageByOwnerId : undefined;
  let canManage =
    resolvedAccess && ownerPractitionerId
      ? resolvedAccess.get(ownerPractitionerId) === true
      : !isInvitedEvent;

  if (isIntakeAppointment(appointment)) {
    canManage = false;
  }

  let isPrivacyRestricted = false;
  if (
    options.readablePatientIds &&
    patientId &&
    !options.readablePatientIds.has(patientId)
  ) {
    isPrivacyRestricted = true;
    clientDisplay = undefined;
    clients = undefined;
    title = 'Busy';
  }

  const videoLink = getVideoMeetingUrl(appointment);

  const candidate: ProviderCalendarEvent = {
    id: appointment.id,
    title,
    description: isPrivacyRestricted ? undefined : appointment.description?.trim() || undefined,
    start,
    end,
    type: eventType,
    color,
    originalDisplayColor: color,
    client: clientDisplay,
    provider: providerLabel,
    status,
    modality,
    sessionTypeId: sessionHealthcareServiceId ?? '',
    ...(serviceTypeDisplay ? { sessionTypeDisplay: serviceTypeDisplay } : {}),
    serviceTypeCode,
    patientId: isPrivacyRestricted ? undefined : patientId,
    practitionerId,
    ownerPractitionerId,
    canManage,
    episodeOfCareId: isPrivacyRestricted ? undefined : resolveEpisodeOfCareId(appointment),
    locationLabel,
    siteId,
    roomId,
    deviceIds,
    appointmentCategory,
    additionalParticipants: isPrivacyRestricted ? undefined : additionalParticipants,
    ...(therapyGroupId ? { therapyGroupId } : {}),
    ...(therapyGroupDisplay && !isPrivacyRestricted ? { therapyGroupDisplay } : {}),
    ...(clients && clients.length > 0 ? { clients } : {}),
    ...(videoLink ? { videoLink } : {}),
    ...(isInvitedEvent || !canManage ? { isInvitedEvent: true } : {}),
    ...(isPrivacyRestricted ? { isPrivacyRestricted: true } : {}),
  };

  const parsed = providerCalendarEventSchema.safeParse(candidate);
  if (!parsed.success) {
    console.warn('[appointmentToCalendarEvent] mapped event failed validation', parsed.error.flatten());
    return null;
  }
  return parsed.data as ProviderCalendarEvent;
}

/**
 * Batch-map Appointment resources, dropping anything un-renderable.
 */
export function appointmentsToCalendarEvents(
  appointments: Appointment[],
  options: AppointmentMapperOptions = {},
): ProviderCalendarEvent[] {
  const out: ProviderCalendarEvent[] = [];
  for (const appt of appointments) {
    const ev = appointmentToCalendarEvent(appt, options);
    if (ev) out.push(ev);
  }
  return out;
}
